// Даты и деньги словами. Экраны 09, 10, 11. SMY-75
//
// Отдельным модулем, потому что читают их четыре экрана аккаунта, а правила
// у них общие и неочевидные.
//
// Приехал из `apps/web/src` 07.09.2026 вместе с `markdown.ts`: правила
// одинаковы на обеих платформах, а лежали они в вебе только потому, что
// клиент был один. Место здесь, а не в токенах: рядом уже живут `formatUnit`,
// `pluralize` и `humanSize` — то же самое, только про другие величины. «2 дня назад» вместо «4 сентября» — не украшение:
// таблица истории отвечает на вопрос «когда это было относительно сейчас», и
// абсолютная дата заставляет считать в уме.

import { pluralize, type UnitLabel } from './units.ts'

const DAY: UnitLabel = { one: 'день', few: 'дня', many: 'дней' }
const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
const DATE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const DATE_YEAR = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * Календарных суток между двумя моментами — по МЕСТНОМУ времени.
 *
 * Не разница в миллисекундах, делённая на сутки: между 23:50 и 00:10 проходит
 * двадцать минут, но это уже «вчера», и человек называет это именно так.
 */
const daysBetween = (at: Date, now: Date): number => {
  const a = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.round((b - a) / 86_400_000)
}

/**
 * «16 дней», «21 день», «4 дня».
 *
 * Здесь, а не в каждом месте, где понадобилось. Правило согласования решает
 * последняя цифра, а исключение — предпоследняя (11–14), и написанное по
 * памяти оно ломается ровно на числах, которые редко встречаются в отладке.
 * Первым его переписал экран ёмкости (SMY-191), вторым — тревога в лог; на
 * третьем разе копии разъехались бы.
 */
export const daysLabel = (n: number): string => `${n} ${pluralize(n, DAY)}`

/**
 * Когда это было. «сегодня, 14:12», «вчера, 19:40», «3 дня назад».
 *
 * Часы показываются только у сегодня и вчера: на третий день время суток уже
 * ничего не уточняет, а строку удлиняет. Дальше недели — обычная дата, потому
 * что «девятнадцать дней назад» считать в уме тяжелее, чем прочитать число.
 */
export function whenLabel(iso: string | undefined, now: Date = new Date()): string {
  if (iso == null) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''

  const days = daysBetween(at, now)
  if (days <= 0) return `сегодня, ${TIME.format(at)}`
  if (days === 1) return `вчера, ${TIME.format(at)}`
  if (days <= 7) return `${days} ${pluralize(days, DAY)} назад`
  return at.getFullYear() === now.getFullYear() ? DATE.format(at) : DATE_YEAR.format(at)
}

/** То же покороче — для карточки проекта, где строка уже занята. */
export function agoLabel(iso: string | undefined, now: Date = new Date()): string {
  if (iso == null) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''

  const days = daysBetween(at, now)
  if (days <= 0) return 'сегодня'
  if (days === 1) return 'вчера'
  if (days <= 7) return `${days} ${pluralize(days, DAY)} назад`
  return at.getFullYear() === now.getFullYear() ? DATE.format(at) : DATE_YEAR.format(at)
}

/**
 * Число с разрядами через неразрывный пробел: «20 000», разорванное
 * переносом, читается двумя числами. Одно правило на цены и счёт знаков у поля
 * (SMY-619, ворота, dedup-hunter).
 */
export const groupDigits = (n: number): string => n.toLocaleString('ru-RU').replace(/\s/g, ' ')

/**
 * Рубли с неразрывным пробелом перед знаком.
 *
 * Разряды тоже неразрывными: «1 990 ₽», разорванное переносом на «1» и
 * «990 ₽», читается как другая цена.
 */
export const rub = (amount: number): string => `${groupDigits(amount)} ₽`

/**
 * Сегодняшний день строкой для постановки модели: «Сегодня — 23 октября 2026 г.».
 * SMY-532, 23.10.2026
 *
 * Модель не знает, какой сегодня день, и берёт год из своих обучающих данных:
 * на замере Э0 ответы в октябре 2026 шли «актуально на май 2025». Сутки —
 * московские, а не сервера: часовой пояс сервера не настройка продукта, и
 * в 21:30 UTC 31 декабря у человека в Москве уже новый год.
 */
let moscowDay: Intl.DateTimeFormat | undefined

export const todayLine = (now: Date = new Date()): string => {
  // Форматтер с часовым поясом заводится при первом вызове, а не при загрузке
  // модуля: ядро грузит и приложение (Hermes), и отказ Intl там уронил бы весь
  // импорт ядра на холодном старте, хотя дата нужна только серверу.
  moscowDay ??= new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow' })
  const day = moscowDay.format(now)
  // ru-RU кончает год сокращением «г.» — вторая точка была бы лишней.
  return `Сегодня — ${day}${day.endsWith('.') ? '' : '.'}`
}

/** Дата обнуления лимитов и продления тарифа: «3 октября». */
export const dayLabel = (iso: string | undefined): string =>
  iso == null ? '' : DATE.format(new Date(iso))

/**
 * Сколько секунд осталось до момента. Пауза до нового кода на экране 01.
 *
 * Считается от ЧАСОВ, а не от числа прошедших тиков, и в этом вся суть.
 * Раньше экран держал у себя число и уменьшал его на единицу раз в секунду —
 * то есть отсчитывал не время, а свои срабатывания. Уйдя в другое
 * приложение, человек забирал таймер с собой: система усыпляет таймеры
 * свёрнутого приложения, и вернувшийся через семь минут видел на кнопке
 * четыре — ждать ему предлагалось второй раз то, что сервер уже отсчитал.
 *
 * Округление ВВЕРХ: при 400 мс до срока честнее показать «1 с», чем ноль
 * рядом с кнопкой, которую сервер ещё не примет.
 */
export const secondsUntil = (deadline: number, now: number = Date.now()): number =>
  Math.max(0, Math.ceil((deadline - now) / 1000))

/**
 * Остаток паузы словами: «45 с», «6:30».
 *
 * Секундами до минуты и «минуты:секунды» дальше. «через 420 с» человек в уме
 * не переводит, а пауза до нового кода семиминутная — одними секундами она
 * читается как ошибка.
 */
export function countdownLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} с`
  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  return `${min}:${String(sec).padStart(2, '0')}`
}

/**
 * «1 минуты», «5 минут» — срок доставки письма после «до». 21.09.2026
 *
 * Родительный падеж после «до»: одна минута — «минуты», остальные — «минут».
 * Экраны входа обеих платформ подставляли число к «минут», и при сроке в минуту
 * писали «до 1 минут» (найдено живой проверкой окна оплаты гостя, SMY-341).
 * Меньше минуты округляется до одной: «до 0 минут» читалось бы «уже пришло».
 */
export function deliveryLabel(seconds: number): string {
  const n = Math.max(1, Math.round(seconds / 60))
  return `${n} ${pluralize(n, { one: 'минуты', few: 'минут', many: 'минут' })}`
}

const MINUTE: UnitLabel = { one: 'минуту', few: 'минуты', many: 'минут' }
const HOUR: UnitLabel = { one: 'час', few: 'часа', many: 'часов' }

/**
 * Когда повторить, словами: «через минуту», «через 12 минут», «через 5 часов».
 * SMY-231
 *
 * Для отказов потолка, срок которого сервер знает точно, а текст пишет сам:
 * окно суток не укладывается в «попробуй через минуту» ограничителя частоты.
 * Точные секунды уходят заголовком `retry-after`, а словами — с округлением
 * ВВЕРХ, как у `secondsUntil`: «через 2 часа» на час с минутой честнее, чем
 * «через час», после которого снова откажут.
 */
export function retryLabel(seconds: number): string {
  if (seconds <= 60) return 'через минуту'
  if (seconds < 3600) {
    const min = Math.ceil(seconds / 60)
    return min === 60 ? 'через час' : `через ${min} ${pluralize(min, MINUTE)}`
  }
  const hours = Math.ceil(seconds / 3600)
  return hours === 1 ? 'через час' : `через ${hours} ${pluralize(hours, HOUR)}`
}
